import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { message, Modal, Tooltip } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Video,
} from "lucide-react";
import TrainingModuleEditIcon from "../../../../Images/trainingModuleEditIcon.png";
import TrainingModuleDeleteIcon from "../../../../Images/trainingModuleDeleteIcon.png";
import TrainingModuleTimingIcon from "../../../../Images/trainingModuleTimingIcon.png";
import TrainingModuleLanguageIcon from "../../../../Images/trainingModuleLanguageIcon.png";
import {
  DELETE_COURSE,
  GET_COURSES,
  isSchemaMissingError,
  trainingApiAvailability,
} from "../../../../services/userAPI";

const pad2 = (n) => String(n ?? 0).padStart(2, "0");

const STATUS_STYLES = {
  ACTIVE: { label: "Active", badge: "bg-[#1FA55A] text-white" },
  DRAFT:  { label: "Draft",  badge: "bg-[#DFDFDF] text-[#666666]" },
};

const formatPublishDate = (dateStr) => {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y}`;
};

const normalizeCourse = (c) => ({
  id: c.id,
  title: c.courseName || c.title || "",
  description: c.courseDescription || c.description || "",
  productStatus:
    c.productStatus ||
    (c.publishDate && c.publishDate > new Date().toISOString().split("T")[0]
      ? "DRAFT"
      : "ACTIVE"),
  duration:
    c.totalLengthMins != null
      ? `${pad2(c.totalLengthMins)}:${pad2(c.totalLengthSecs)} Min`
      : c.duration || "00:00 Min",
  videoCount: c.videoCount ?? c.videos?.length ?? 0,
  language: c.language || "",
  publishDate: c.publishDate || "",
  thumbnailPreview: c.thumbnailImage_ || c.thumbnailUrl || c.thumbnailPreview || "",
  thumbnailUrl: c.thumbnailUrl || "",
  keyPoints: c.keyPoints || [],
  points: c.points || (c.keyPoints?.map((k) => k.keyPoint) ?? []),
});

const TrainingModule = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const ROLE_ID = location.state?.roleId ? parseInt(location.state.roleId) : null;
  const ACCOUNT_ID = location.state?.accountId ? parseInt(location.state.accountId) : null;

  const [filterStatus, setFilterStatus] = useState("active");
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleteCourse] = useMutation(DELETE_COURSE);

  const { data: coursesData, loading: isLoading, refetch } = useQuery(GET_COURSES, {
    variables: {
      accountId: ACCOUNT_ID,
      jobroleId: ROLE_ID,
      pagingCriteria: {
        limit: 50,
        cursor: null,
        sortByField: "created_at",
        sortOrder: "desc",
      },
    },
    skip: !ACCOUNT_ID || !trainingApiAvailability.getCourses,
    fetchPolicy: "network-only",
    onError: (err) => {
      if (isSchemaMissingError(err)) trainingApiAvailability.getCourses = false;
    },
  });

  const modules = (coursesData?.getCourses?.data || []).map(normalizeCourse);

  const updates = modules.filter(
    (m) => m.productStatus === filterStatus.toUpperCase()
  );

  const totalVideos = modules.reduce((s, m) => s + (m.videoCount || 0), 0);
  const draftCount = modules.filter((m) => m.productStatus === "DRAFT").length;

  const goToAddPage = (editing = null) =>
    navigate("/add-course-module", { state: { ...location.state, editing } });

  const goToDetail = (course) =>
    navigate("/course-module-detail", { state: { ...location.state, course } });

  const handleDelete = () => {
    const courseId = deleteCandidate?.id;

    if (courseId && trainingApiAvailability.deleteCourse) {
      deleteCourse({ variables: { courseId } })
        .then((res) => {
          if (res?.data?.deleteCourse?.success === false)
            throw new Error(res.data.deleteCourse.message || "Delete failed");
          message.success("Module deleted successfully!");
          refetch();
        })
        .catch((err) => {
          if (isSchemaMissingError(err)) {
            trainingApiAvailability.deleteCourse = false;
            message.success("Module deleted successfully!");
          } else {
            message.error("Delete failed: " + err.message);
          }
        });
    }
    setDeleteCandidate(null);
  };

  const statCards = [
    { label: "Total Courses", value: modules.length, bg: "bg-[#E5FDEE]", border: "border-[#1FA551]", text: "text-[#1FA551]" },
    { label: "Total Videos",  value: totalVideos,    bg: "bg-[#F5EDFF]", border: "border-[#9747FF]", text: "text-[#9747FF]" },
    { label: "Draft",         value: draftCount,     bg: "bg-[#ECF5FF]", border: "border-[#1376EB]", text: "text-[#1376EB]" },
  ];

  return (
    <div className="rounded-[16px] bg-white p-5">
      <h1 className="mb-5 font-['Inter'] text-[20px] font-semibold leading-[100%] tracking-[-0.2px] text-black">
        Training Module
      </h1>

      {/* Stat cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        {statCards.map((card) => (
          <div
            key={card.label}
            className={`flex h-[92px] flex-col gap-1 rounded-[12px] border ${card.border} ${card.bg} px-3 pb-6 pt-3`}
          >
            <span className={`font-['Inter'] text-[14px] font-semibold ${card.text}`}>
              {card.label}
            </span>
            <span className={`font-['Inter'] text-[28px] font-semibold leading-[100%] ${card.text}`}>
              {card.value}
            </span>
          </div>
        ))}
      </div>

      {/* Filters + Add button */}
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setFilterStatus("active")}
            className={`rounded-[8px] px-6 py-3 text-[14px] font-semibold transition-colors ${
              filterStatus === "active"
                ? "bg-[#1FA55A] text-white"
                : "bg-white text-[#666] hover:bg-gray-50"
            }`}
          >
            Active
          </button>
          <button
            type="button"
            onClick={() => setFilterStatus("draft")}
            className={`rounded-[8px] px-6 py-3 text-[14px] font-semibold transition-colors ${
              filterStatus === "draft"
                ? "bg-[#054DA2] text-white"
                : "bg-[#DFDFDF] text-[#666] hover:bg-gray-300"
            }`}
          >
            Draft
          </button>
        </div>
        <button
          type="button"
          onClick={() => goToAddPage()}
          className="flex items-center justify-center rounded-[8px] bg-[#AD0101] px-6 py-4 text-[14px] font-semibold text-white transition-colors hover:bg-[#8C0101]"
        >
          Add Training Module
        </button>
      </div>

      <div className="border-t border-[#E8E8E8]" />

      {/* Cards list */}
      <div className="mt-5 flex flex-col gap-4">
        {isLoading ? (
          <div className="flex min-h-[120px] items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#AD0101] border-t-transparent" />
          </div>
        ) : updates.length === 0 ? (
          <p className="py-16 text-center font-['Inter'] text-[14px] text-[#A0A0A0]">
            No Data Available
          </p>
        ) : (
          updates.map((update) => {
            const status = STATUS_STYLES[update.productStatus] || STATUS_STYLES.DRAFT;
            return (
              <div
                key={update.id}
                className="flex items-center justify-between rounded-[16px] border border-[#E8E8E8] bg-white p-5"
              >
                <div
                  className="flex min-w-0 cursor-pointer items-center gap-4"
                  onClick={() => goToDetail(update)}
                >
                  <div className="flex h-[115px] w-[146px] shrink-0 items-center justify-center rounded-[12px] border border-[#CBC8C8] bg-white p-2">
                    {update.thumbnailPreview && (
                      <img
                        src={update.thumbnailPreview}
                        alt=""
                        className="max-h-full max-w-full object-contain"
                      />
                    )}
                  </div>
                  <div className="flex min-w-0 flex-col gap-1.5">
                    {update.productStatus === "DRAFT" ? (
                      <span className="inline-flex w-fit items-center rounded-[6px] bg-[#054DA2] px-[10px] py-1 text-[12px] font-semibold text-white">
                        Yet to publish on {formatPublishDate(update.publishDate)}
                      </span>
                    ) : (
                      <span className={`inline-flex w-fit items-center rounded-[6px] px-[10px] py-1 text-[12px] font-semibold ${status.badge}`}>
                        {status.label}
                      </span>
                    )}
                    <h3 className="font-['Inter'] text-[18px] font-semibold leading-[21px] text-[#101828]">
                      {update.title}
                    </h3>
                    <Tooltip title={update.description} placement="topLeft">
                      <p className="max-w-[398px] cursor-default truncate text-[14px] leading-[150%] text-[#9CA3AF]">
                        {update.description}
                      </p>
                    </Tooltip>
                    <div className="flex items-center gap-4">
                      {update.duration && (
                        <span className="flex items-center gap-1 font-['Inter'] text-[14px] leading-[18px] text-[#888888]">
                          <img src={TrainingModuleTimingIcon} alt="" className="h-4 w-4 object-contain" />
                          {update.duration}
                        </span>
                      )}
                      <span className="flex items-center gap-1 font-['Inter'] text-[14px] leading-[18px] text-[#888888]">
                        <Video size={15} className="text-[#888888]" strokeWidth={1.8} />
                        {update.videoCount} Videos
                      </span>
                      {update.language && (
                        <span className="flex items-center gap-1 font-['Inter'] text-[14px] leading-[18px] text-[#888888]">
                          <img src={TrainingModuleLanguageIcon} alt="" className="h-4 w-4 object-contain" />
                          {update.language}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2 self-center">
                  <button type="button" onClick={() => goToAddPage(update)} title="Edit">
                    <img src={TrainingModuleEditIcon} alt="Edit" className="h-8 w-8 object-contain" />
                  </button>
                  <button type="button" onClick={() => setDeleteCandidate(update)} title="Delete">
                    <img src={TrainingModuleDeleteIcon} alt="Delete" className="h-8 w-8 object-contain" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {updates.length > 0 && (
        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" disabled className="p-1 text-gray-300"><ChevronsLeft size={18} strokeWidth={2.5} /></button>
          <button type="button" disabled className="p-1 text-gray-300"><ChevronLeft  size={18} strokeWidth={2.5} /></button>
          <button type="button" disabled className="p-1 text-gray-300"><ChevronRight size={18} strokeWidth={2.5} /></button>
          <button type="button" disabled className="p-1 text-gray-300"><ChevronsRight size={18} strokeWidth={2.5} /></button>
        </div>
      )}

      {/* Delete confirmation */}
      <Modal open={!!deleteCandidate} onCancel={() => setDeleteCandidate(null)} footer={null} centered width={420}>
        <div className="p-2">
          <h3 className="mb-2 text-lg font-semibold text-[#1F2020]">
            Are you sure you want to delete the Course?
          </h3>
          <p className="mb-6 text-sm text-[#888888]">
            Are you sure you want to delete "{deleteCandidate?.title}"? This action will permanently
            remove the course and all its associated videos.
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setDeleteCandidate(null)}
              className="rounded-lg border border-gray-300 px-6 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="rounded-lg bg-[#AD0101] px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-[#8C0101]"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TrainingModule;
